/**
 * ChatMessage component - renders a single message bubble in the chat conversation.
 * Supports user, assistant, and error message roles with markdown rendering.
 */
import { clsx } from 'clsx';
import type { ChatMessageData } from '../../types/chat';

export interface ChatMessageProps {
  message: ChatMessageData;
  isStreaming?: boolean;
}

/**
 * Format a timestamp as a short relative or absolute time.
 * Shows "Just now" for <1min, "Xm ago" for <1h, otherwise HH:MM.
 */
function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;

  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Minimal markdown renderer for assistant messages.
 * Supports: **bold**, `inline code`, ```code blocks```, and bullet lists (- item).
 */
function renderMarkdown(text: string): React.ReactNode[] {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i]!;

    // Fenced code block
    if (line.trimStart().startsWith('```')) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i]!.trimStart().startsWith('```')) {
        codeLines.push(lines[i]!);
        i++;
      }
      i++; // skip closing ```
      elements.push(
        <pre
          key={`code-${elements.length}`}
          className="my-2 overflow-x-auto rounded bg-dark-900/60 p-2 text-xs font-mono text-dark-200"
        >
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      continue;
    }

    // Bullet list item
    if (/^\s*[-*]\s/.test(line)) {
      const listItems: React.ReactNode[] = [];
      while (i < lines.length && /^\s*[-*]\s/.test(lines[i]!)) {
        listItems.push(
          <li key={`li-${listItems.length}`}>
            {renderInline(lines[i]!.replace(/^\s*[-*]\s/, ''))}
          </li>
        );
        i++;
      }
      elements.push(
        <ul key={`ul-${elements.length}`} className="my-1 list-disc pl-4 space-y-0.5">
          {listItems}
        </ul>
      );
      continue;
    }

    // Regular paragraph line
    if (line.trim() === '') {
      elements.push(<br key={`br-${elements.length}`} />);
    } else {
      elements.push(
        <p key={`p-${elements.length}`} className="my-0.5">
          {renderInline(line)}
        </p>
      );
    }
    i++;
  }

  return elements;
}

/**
 * Render inline markdown: **bold** and `code`.
 */
function renderInline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  // Match **bold** or `code`
  const regex = /(\*\*(.+?)\*\*|`([^`]+)`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    // Push text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[2]) {
      // Bold
      parts.push(
        <strong key={`b-${parts.length}`} className="font-semibold">
          {match[2]}
        </strong>
      );
    } else if (match[3]) {
      // Inline code
      parts.push(
        <code
          key={`c-${parts.length}`}
          className="rounded bg-dark-900/50 px-1 py-0.5 text-xs font-mono text-primary-300"
        >
          {match[3]}
        </code>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

/** Animated typing indicator (three bouncing dots) */
function TypingIndicator() {
  return (
    <div className="flex items-center gap-1" aria-label="Bot is typing">
      <span className="h-2 w-2 animate-bounce rounded-full bg-dark-400 [animation-delay:0ms]" />
      <span className="h-2 w-2 animate-bounce rounded-full bg-dark-400 [animation-delay:150ms]" />
      <span className="h-2 w-2 animate-bounce rounded-full bg-dark-400 [animation-delay:300ms]" />
    </div>
  );
}

export function ChatMessage({ message, isStreaming }: ChatMessageProps) {
  const { role, content, timestamp, tradeConfirmation, attachments } = message;

  const isUser = role === 'user';
  const isError = role === 'error';
  const isAssistant = role === 'assistant';

  // Show typing indicator when streaming and content is empty
  const showTyping = isStreaming && !content;

  return (
    <div
      className={clsx('flex w-full', isUser ? 'justify-end' : 'justify-start')}
      data-testid={`chat-message-${role}`}
    >
      <div
        className={clsx(
          'max-w-[85%] rounded-lg px-3 py-2 text-sm',
          isUser && 'ml-auto bg-primary-600 text-white',
          isAssistant && 'mr-auto bg-dark-700/80 text-dark-100',
          isError && 'mr-auto bg-error-900/40 border border-error-700/50 text-error-200'
        )}
      >
        {/* Error icon for error messages */}
        {isError && (
          <div className="mb-1 flex items-center gap-1.5 text-error-400">
            <svg
              className="h-4 w-4 flex-shrink-0"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-xs font-medium">Error</span>
          </div>
        )}

        {/* Message content */}
        {showTyping ? (
          <TypingIndicator />
        ) : (
          <div className={clsx(isAssistant && 'prose-chat')}>
            {isAssistant ? renderMarkdown(content) : content}
          </div>
        )}

        {/* Attachment thumbnails */}
        {attachments && attachments.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {attachments.map((att) => (
              <div key={att.id} className="flex items-center gap-1.5 rounded bg-dark-900/40 px-2 py-1">
                {att.previewUrl ? (
                  <img
                    src={att.previewUrl}
                    alt={att.name}
                    className="h-10 w-10 rounded object-cover"
                  />
                ) : (
                  <svg className="h-4 w-4 text-dark-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                )}
                <span className="max-w-[100px] truncate text-[10px] opacity-75">{att.name}</span>
              </div>
            ))}
          </div>
        )}

        {/* Trade confirmation placeholder */}
        {tradeConfirmation && (
          <div
            className="mt-2 rounded border border-primary-700/50 bg-dark-800/60 p-2 text-xs text-dark-300"
            data-testid="trade-confirmation-placeholder"
          >
            📊 Trade Confirmation: {tradeConfirmation.action} {tradeConfirmation.quantity}{' '}
            {tradeConfirmation.symbol} @ ${tradeConfirmation.price.toFixed(2)}
          </div>
        )}

        {/* Timestamp */}
        <time
          className={clsx(
            'mt-1 block text-[10px]',
            isUser && 'text-primary-200/70',
            isAssistant && 'text-dark-400',
            isError && 'text-error-400/70'
          )}
          dateTime={timestamp}
        >
          {formatTimestamp(timestamp)}
        </time>
      </div>
    </div>
  );
}
